import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Alerts, InlineAlert } from '@nextroll/ar-style-base';

import { CrmOptionType } from 'types/rofl';


interface IProps {
    advertisableEid?: string;
    crm?: CrmOptionType;
    updateCrm: (advertisableEid?: string) => void;
}

export const AccountActionCrmNoConnectedSection = ({ advertisableEid, crm, updateCrm }: IProps) => {
    /**
     * Variable that marks the setInterval and prevent the page from throttling.
     */
    const heartbeatInterval = useRef<NodeJS.Timer>();

    /**
     * Start the default CRM check heartbeat if the account-based actin panel is visible.
     * If the user is watching the canvas, then we check the auth status regularly until the CRM is properly set.
     */
    const startHeartbeat = useCallback(() => {
        if (!advertisableEid || (crm && crm !== 'other')) {
            return; // default CRM is properly set already; do nothing
        }
        heartbeatInterval.current && clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = setInterval(() => updateCrm(advertisableEid), 15000);
        updateCrm(advertisableEid);
    }, [advertisableEid, crm, updateCrm]);

    /**
     * Stop the CRM check heartbeat when the account-based actin panel is not visible to avoid unnecessary requests.
     */
    const stopHeartbeat = useCallback(() => heartbeatInterval.current && clearInterval(heartbeatInterval.current), []);

    useEffect(() => {
        crm && crm !== 'other' && stopHeartbeat(); // stop the heartbeat check after CRM is set
    }, [crm, stopHeartbeat]);

    /**
     * Register onFocus and onBlur listeners
     */
    useEffect(() => {
        window.addEventListener('focus', startHeartbeat);
        window.addEventListener('blur', stopHeartbeat);
        return () => {
            stopHeartbeat(); // make sure to stop any heartbeat request and then remove event listeners
            window.removeEventListener('focus', startHeartbeat);
            window.removeEventListener('blur', stopHeartbeat);
        };
    }, [startHeartbeat, stopHeartbeat]);

    const content = useMemo(() => (
        <>
            <p>{i18n.gettext('Select your Customer Relationship Management platform to connect.')}</p>
            <div className='workflow-node-panel-account-action-crm-options mt-3'>
                <a
                    className='ar-card-button btn-block'
                    href={`/settings/salesforce?advertisable=${advertisableEid}`}
                    target='_blank'
                    rel='noreferrer noopener'
                    onClick={startHeartbeat}
                >
                    <div className='fa-stack fa-sm mr-1'>
                        <i className='fab fa-salesforce fa-stack-2x' />
                    </div>
                    <h4>Salesforce</h4>
                    <span style={{ flex: 1 }} />
                    <i className='chevron fas fa-chevron-right' />
                </a>
                <a
                    className='ar-card-button btn-block'
                    href={`/settings/hubspot?advertisable=${advertisableEid}`}
                    target='_blank'
                    rel='noreferrer noopener'
                    onClick={startHeartbeat}
                >
                    <div className='fa-stack fa-sm mr-1'>
                        <i className='fa fa-circle fa-stack-2x' />
                        <i className='fab fa-hubspot fa-stack-1x fa-inverse' />
                    </div>
                    <h4>HubSpot</h4>
                    <span style={{ flex: 1 }} />
                    <i className='chevron fas fa-chevron-right' />
                </a>
            </div>
        </>
    ), [advertisableEid, startHeartbeat]);

    if (!advertisableEid) {
        return null;
    }

    return (
        <InlineAlert
            type={Alerts.ALERT_TYPE_INFO}
            title={i18n.gettext('This action requires a CRM integration')}
            message={content as any} // TODO: need to update ar-style-base InlineAlert prop type to ReactNode
            showIcon
        />
    );
};
